import React, { useEffect, useRef, useState } from "react";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import servicesData from "../../data/services.json";
import {
  Sparkles,
  Compass,
  PenTool,
  MapPin,
  BarChart3,
  FileText,
  RefreshCw,
  ChevronDown,
} from "lucide-react";

const ICON_MAP = {
  Compass,
  PenTool,
  MapPin,
  BarChart3,
  FileText,
  RefreshCw,
};

/* ─────────────────────────────────────────────
 PROCESS STEP CARD
───────────────────────────────────────────── */
const ProcessStep = ({ step, index, isActive, onClick }) => {
  const cardRef = useRef(null);
  const iconRef = useRef(null);
  const contentRef = useRef(null);
  const lineRef = useRef(null);

  const IconC = ICON_MAP[step.icon] || Compass;

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        cardRef.current,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          delay: index * 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: cardRef.current,
            start: "top 85%",
          },
        },
      );
    }, cardRef);
    return () => ctx.revert();
  }, [index]);

  /* Handle open/close animation */
  useEffect(() => {
    if (isActive) {
      gsap.to(contentRef.current, {
        height: "auto",
        opacity: 1,
        duration: 0.5,
        ease: "power2.out",
      });
      gsap.to(iconRef.current, {
        rotate: 360,
        backgroundColor: step.color,
        color: "#fff",
        duration: 0.5,
      });
      gsap.to(lineRef.current, {
        scaleY: 1,
        duration: 0.5,
        ease: "power2.out",
      });
    } else {
      gsap.to(contentRef.current, {
        height: 0,
        opacity: 0,
        duration: 0.4,
        ease: "power2.in",
      });
      gsap.to(iconRef.current, {
        rotate: 0,
        backgroundColor: `${step.color}20`,
        color: step.color,
        duration: 0.5,
      });
      gsap.to(lineRef.current, {
        scaleY: 0,
        duration: 0.4,
      });
    }
  }, [isActive, step.color]);

  return (
    <div
      ref={cardRef}
      className="relative pl-12 md:pl-16 group cursor-pointer"
      onClick={onClick}
    >
      {/* ── Timeline Line ── */}
      <div className="absolute left-[20px] md:left-[28px] top-12 bottom-0 w-px bg-white/5" />
      <div
        ref={lineRef}
        className="absolute left-[20px] md:left-[28px] top-12 bottom-0 w-px origin-top transform-gpu scale-y-0"
        style={{
          background: `linear-gradient(to bottom, ${step.color}, transparent)`,
        }}
      />

      {/* ── Icon / Number Node ── */}
      <div
        ref={iconRef}
        className="absolute left-0 top-0 w-10 md:w-14 h-10 md:h-14 rounded-2xl flex items-center justify-center transition-colors shadow-lg z-10"
        style={{
          backgroundColor: `${step.color}20`,
          border: `1px solid ${step.color}40`,
          color: step.color,
        }}
      >
        <span className="absolute -top-2 -left-2 text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-white border border-white/10 text-white">
          {step.number}
        </span>
        <IconC size={20} className="stroke-current" />
      </div>

      {/* ── Header ── */}
      <div className="bg-white/5 hover:bg-white/10 transition-colors duration-300 rounded-2xl p-5 md:p-6 border border-white/5">
        <div className="flex items-center justify-between">
          <h3
            className="text-xl md:text-2xl font-bold text-slate-800 font-display transition-colors"
            style={{ color: isActive ? step.color : "#1e293b" }}
          >
            {step.title}
          </h3>
          <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0">
            <ChevronDown
              size={14}
              className={`text-slate-400 transition-transform duration-300 ${isActive ? "rotate-180" : ""}`}
            />
          </div>
        </div>

        {/* ── Expanding Content ── */}
        <div ref={contentRef} className="overflow-hidden h-0 opacity-0">
          <div className="pt-4 space-y-5">
            <p className="text-slate-600 text-sm md:text-base leading-relaxed">
              {step.description}
            </p>

            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-3">
                Key Deliverables
              </p>
              <div className="flex flex-wrap gap-2">
                {step.deliverables.map((del, i) => (
                  <span
                    key={i}
                    className="text-xs px-3 py-1.5 rounded-md font-medium"
                    style={{
                      backgroundColor: `${step.color}15`,
                      color: step.color,
                    }}
                  >
                    {del}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 MAIN PROCESS COMPONENT
───────────────────────────────────────────── */
const ServiceProcess = () => {
  const { process } = servicesData;
  const [activeStep, setActiveStep] = useState(1);

  const sectionRef = useRef(null);
  const headRef = useRef(null);
  const labelRef = useRef(null);
  const descRef = useRef(null);
  const imgWrapRef = useRef(null);
  const imgRef = useRef(null);

  /* Header animations */
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 75%",
        },
      });

      tl.fromTo(
        labelRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5 },
      )
        .fromTo(
          headRef.current?.children ?? [],
          { y: 40, opacity: 0, skewY: 2 },
          {
            y: 0,
            opacity: 1,
            skewY: 0,
            stagger: 0.1,
            duration: 0.7,
            ease: "power4.out",
          },
          "-=0.2",
        )
        .fromTo(
          descRef.current,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6 },
          "-=0.3",
        );

      /* Sticky Image Parallax */
      if (imgRef.current && imgWrapRef.current) {
        gsap.to(imgRef.current, {
          yPercent: 20,
          ease: "none",
          scrollTrigger: {
            trigger: imgWrapRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="methodology"
      className="relative p-16 bg-white overflow-hidden"
    >
      {/* Background Noise & Overlay */}
      <div className="absolute inset-0 dot-pattern opacity-10 pointer-events-none" />
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-primary-600/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container-fluid relative z-10 px-4 md:px-8 max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-16 md:mb-24">
          <div ref={headRef} className="space-y-1 mb-6">
            <h2 className="font-display font-black text-slate-800 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-none">
              {process.headline}
            </h2>
            <h2 className="font-display font-black text-gradient-primary text-4xl md:text-5xl lg:text-6xl tracking-tight leading-none">
              {process.headlineAccent}
            </h2>
          </div>

          <p ref={descRef} className="text-slate-600 text-base md:text-lg">
            {process.description}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left: Interactive Timeline */}
          <div className="space-y-6">
            {process.steps.map((step, idx) => (
              <ProcessStep
                key={step.id}
                step={step}
                index={idx}
                isActive={activeStep === step.id}
                onClick={() =>
                  setActiveStep(activeStep === step.id ? null : step.id)
                }
              />
            ))}
          </div>

          {/* Right: Sticky Graphical Display */}
          <div className="hidden lg:block">
            <div className="sticky top-32">
              <div
                ref={imgWrapRef}
                className="relative rounded-3xl overflow-hidden shadow-2xl glass border border-white/10"
                style={{ aspectRatio: "4/5" }}
              >
                {/* Dynamic Image Overlay based on active step color */}
                <div
                  className="absolute inset-0 z-10 opacity-60 mix-blend-color transition-colors duration-700"
                  style={{
                    backgroundColor: activeStep
                      ? process.steps.find((s) => s.id === activeStep)?.color
                      : "#1d4ed8",
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#060d24] via-[#060d24]/40 to-transparent z-20" />

                <img
                  ref={imgRef}
                  src="https://images.unsplash.com/photo-1542626991-cbc4e32524cc?w=800&q=80&fit=crop"
                  alt="Methodology"
                  className="absolute inset-0 w-full h-[120%] -top-[10%] object-cover grayscale brightness-75 contrast-125"
                />

                {/* Active Step Infographic Overlay */}
                {activeStep && (
                  <div className="absolute bottom-10 left-10 right-10 z-30">
                    <div
                      className="font-mono text-[100px] font-black leading-none opacity-20 mb-[-20px] select-none"
                      style={{
                        color: process.steps.find((s) => s.id === activeStep)
                          ?.color,
                      }}
                    >
                      {process.steps.find((s) => s.id === activeStep)?.number}
                    </div>
                    <h3 className="font-display font-bold text-3xl text-slate-800">
                      {process.steps.find((s) => s.id === activeStep)?.title}
                    </h3>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceProcess;
